class PredictiveSearch extends HTMLElement {
    constructor() {
        super();

        this.cachedResults = {};
        this.input = this.querySelector('input[type="search"]');
        this.predictiveSearchResults = this.querySelector(
            '[data-predictive-search]'
        );
        this.isOpen = false;
        this.inputClearButton = this.querySelector('[input-clear-button]');

        document.addEventListener(_EVENT_HELPER.hideDrawer, (event) => {
            if (event.detail.drawerType === 'search-drawer') {
                this.close(true);
            }
        });

        this.setupEventListeners();
    }

    setupEventListeners() {
        const form = this.querySelector('form.search');

        form.addEventListener(
            'submit',
            this.onFormSubmit.bind(this)
        );

        this.input.addEventListener(
            'input',
            HELPER_UTIL.debounce((event) => {
                this.onChange(event);
            }, 300).bind(this)
        );

        this.input.addEventListener(
            'focus',
            this.onFocus.bind(this)
        );

        this.addEventListener(
            'keyup',
            this.onKeyup.bind(this)
        );

        this.addEventListener(
            'keydown',
            this.onKeydown.bind(this)
        );

        if (this.inputClearButton) {
            this.inputClearButton.addEventListener(
                'click',
                this.inputClear.bind(this)
            );
        }
    }

    getQuery() {
        return this.input.value.trim();
    }

    onChange() {
        const searchTerm = this.getQuery();

        if (!searchTerm.length) {
            this.close(true);
            return;
        }

        this.getSearchResults(searchTerm);
    }

    onFormSubmit(event) {
        if (
            !this.getQuery().length ||
            this.querySelector('[aria-selected="true"] a')
        ) {
            event.preventDefault();
        }
    }

    onFocus() {
        const searchTerm = this.getQuery();

        if (!searchTerm.length) {
            return;
        }

        if (this.getAttribute('results') === 'true') {
            this.open();
        } else {
            this.getSearchResults(searchTerm);
        }
    }

    onKeyup(event) {
        if (!this.getQuery().length) {
            this.close(true);
        }

        event.preventDefault();

        switch (event.code) {
            case 'ArrowUp':
                this.switchOption('up');
                break;

            case 'ArrowDown':
                this.switchOption('down');
                break;

            case 'Enter':
                this.selectOption();
                break;
        }
    }

    onKeydown(event) {
        // Yön tuşları kullanılırken input imlecinin hareket etmesini engeller.
        if (
            event.code === 'ArrowUp' ||
            event.code === 'ArrowDown'
        ) {
            event.preventDefault();
        }
    }

    switchOption(direction) {
        if (!this.getAttribute('open')) {
            return;
        }

        const moveUp = direction === 'up';

        const selectedElement = this.querySelector(
            '[aria-selected="true"]'
        );

        const allElements = this.querySelectorAll(
            '.predictive-search__list.active .predictive-search__list-item'
        );

        let activeElement = this.querySelector(
            '.predictive-search__list.active .predictive-search__list-item'
        );

        if (!allElements.length) {
            return;
        }

        if (moveUp && !selectedElement) {
            return;
        }

        if (!moveUp && selectedElement) {
            activeElement =
                selectedElement.nextElementSibling ||
                allElements[0];
        } else if (moveUp) {
            activeElement =
                selectedElement.previousElementSibling ||
                allElements[allElements.length - 1];
        }

        if (!activeElement || activeElement === selectedElement) {
            return;
        }

        activeElement.setAttribute('aria-selected', 'true');

        if (selectedElement) {
            selectedElement.setAttribute(
                'aria-selected',
                'false'
            );
        }

        this.input.setAttribute(
            'aria-activedescendant',
            activeElement.id
        );
    }

    selectOption() {
        const selectedProduct = this.querySelector(
            '[aria-selected="true"] a, [aria-selected="true"] button'
        );

        if (selectedProduct) {
            selectedProduct.click();
        }
    }

    getSearchResults(searchTerm) {
        const queryKey = searchTerm
            .toLocaleLowerCase('tr-TR')
            .replace(/\s+/g, '-');

        if (this.cachedResults[queryKey]) {
            this.renderSearchResults(
                this.cachedResults[queryKey]
            );
            return;
        }

        const searchParams = new URLSearchParams({
            q: searchTerm,
            section_id: 'predictive-search',
            'resources[type]':
                'query,product,collection,page,article',
            'resources[limit]': '10',
            'resources[limit_scope]': 'each',

            // Ürün adı, varyant, marka, SKU ve barkod alanlarında ara.
            'resources[options][fields]':
                'title,product_type,variants.title,vendor,variants.sku,variants.barcode'
        });

        fetch(
            `${routes.predictive_search_url}?${searchParams.toString()}`
        )
            .then((response) => {
                if (!response.ok) {
                    const error = new Error(
                        `Predictive search error: ${response.status}`
                    );

                    this.close();
                    throw error;
                }

                return response.text();
            })
            .then((text) => {
                const parsedDocument = new DOMParser().parseFromString(
                    text,
                    'text/html'
                );

                const predictiveSearchSection =
                    parsedDocument.querySelector(
                        '#shopify-section-predictive-search'
                    );

                if (!predictiveSearchSection) {
                    throw new Error(
                        'Predictive search sonuç bölümü bulunamadı.'
                    );
                }

                const resultsMarkup =
                    predictiveSearchSection.innerHTML;

                this.cachedResults[queryKey] = resultsMarkup;
                this.renderSearchResults(resultsMarkup);
            })
            .catch((error) => {
                this.close();
                console.error(error);
            });
    }

    renderSearchResults(resultsMarkup) {
        this.predictiveSearchResults.innerHTML = resultsMarkup;
        this.setAttribute('results', 'true');
        this.activateTab();
        this.open();
    }

    activateTab() {
        this.predictiveSearchResults
            .querySelector('[tab-head]')
            ?.classList.add('active');

        this.predictiveSearchResults
            .querySelector('[tab-body]')
            ?.classList.add('active');
    }

    open() {
        this.setAttribute('open', 'true');
        this.input.setAttribute('aria-expanded', 'true');
        this.isOpen = true;
    }

    close(clearSearchTerm = false) {
        if (clearSearchTerm) {
            this.input.value = '';
            this.removeAttribute('results');
        }

        const selected = this.querySelector(
            '[aria-selected="true"]'
        );

        if (selected) {
            selected.setAttribute(
                'aria-selected',
                'false'
            );
        }

        this.input.setAttribute(
            'aria-activedescendant',
            ''
        );

        this.removeAttribute('open');
        this.input.setAttribute('aria-expanded', 'false');
        this.predictiveSearchResults.removeAttribute('style');
        this.isOpen = false;
    }

    inputClear() {
        this.close(true);
    }
}

if (!customElements.get('predictive-search')) {
    customElements.define(
        'predictive-search',
        PredictiveSearch
    );
}


class PromptElement extends HTMLElement {
    constructor() {
        super();

        this.promptContainer = this.querySelector(
            '[prompt-element]'
        );

        this.init();
    }

    init() {
        if (!this.promptContainer) {
            return;
        }

        const promptCount =
            this.promptContainer.childElementCount;

        this.promptContainer.style.cssText =
            `--prompt-count-steps:steps(${promptCount});` +
            `--prompt-top:${-promptCount * 24}px;` +
            `--prompt-duration:${promptCount * 3}s;`;
    }
}

if (!customElements.get('prompt-element')) {
    customElements.define(
        'prompt-element',
        PromptElement
    );
}